import { action } from '@ember/object';
import type { Registry as Services } from '@ember/service';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';
import { PrismSelectInput } from '@mylighthouse/prism-ember';
import type { IconBrandName } from '@mylighthouse/prism-ember/types/components/prism/icon-brand';
import type { Option } from '@mylighthouse/prism-ember/types/components/prism/select-input';
import type { Ota } from 'frontend-utils/models';
import { OTA_IDS } from 'frontend-utils/utils/constants/constant-values';
import { capitalize } from 'frontend-utils/utils/convert-case';

interface HotelAndMarketDropdownsSelectionOtasSignature {
  Args: {
    includeAllChannels?: boolean;
    onlyAllowMatchedOtas?: boolean;
    onlyAllowOtasWithRoomtypeFilter?: boolean;
    selectOta: (ota: Ota) => void;
    selectedOTA: Ota;
    /**
     * The different products that can call upon this dropdown.
     * It is used to determine which OTAs are available for selection.
     */
    type?: 'rates';
  };
}

type OtaOption = Option & { iconBrand?: IconBrandName; ota: Ota };

export default class HotelAndMarketDropdownsSelectionOtas extends Component<HotelAndMarketDropdownsSelectionOtasSignature> {
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hotel() {
    return this.hotelAndMarketService.hotel;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hotelOrMarket() {
    return this.hotelAndMarketService.hotelOrMarket;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get includeAllChannels() {
    return this.args.includeAllChannels || true;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get market() {
    return this.hotelAndMarketService.market;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get onlyAllowMatchedOtas() {
    return this.args.onlyAllowMatchedOtas || false;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get onlyAllowOtasWithRoomtypeFilter() {
    return this.args.onlyAllowOtasWithRoomtypeFilter || false;
  }

  /**
   * Determines possible OTAs
   */
  @cached // Preserve reference for isEqual check in PowerSelect code
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get options() {
    const otaOptions = this.hotelOrMarket[this.otaKey].map((ota: Ota) => {
      let disabledProperties = {};
      if (!ota.isConnectedOrBrandDotCom) {
        disabledProperties = {
          disabled: true,
          disabledMessage: this.intl.t('rates.disconnected_ota'),
          icon: 'connection',
          iconBrand: undefined,
        };
      }

      return {
        label: ota.name,
        iconBrand: (ota.id as IconBrandName | null) ?? undefined,
        ...disabledProperties,
        ota,
      };
    });

    if (this.market) {
      return otaOptions.sort((a, b) => a.ota.order - b.ota.order);
    }

    // Filter the OTAs based whether they are matched by the own hotel or any competitor
    return otaOptions.filter((option) => {
      if (
        option.ota.id === OTA_IDS.ALL_CHANNELS &&
        !this.args.includeAllChannels
      ) {
        return false;
      }

      if (
        this.onlyAllowOtasWithRoomtypeFilter &&
        !option.ota.hasRoomtypeFilter
      ) {
        return false;
      }

      if (this.onlyAllowMatchedOtas) {
        // Check if the own hotel has the ota matched
        if (this.hotelAndMarketService.isHotelMatched(option.ota.id)) {
          return true;
        }

        // Check if any competitor has the OTA matched
        // We do this by looping over every competitor
        // We create an array of ota ids, these are the otas that any competitor in this compset has a matching for
        // That way we can ensure that there is data for a certain ota
        const competitorOtas =
          this.hotelAndMarketService.primaryAndSecondaryCompetitors.reduce<
            (string | null)[]
          >((otas, hotelinfo) => {
            const hotelinfoOtas = hotelinfo.otas.map(
              (matchedOta) => matchedOta.id,
            );

            return [...otas, ...hotelinfoOtas];
          }, []);
        // Check if the ota is matched for any competitor
        if (competitorOtas.indexOf(option.ota.id) !== -1) {
          return true;
        }

        return false;
      }

      return true;
    });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get otaKey() {
    return `OTAs${capitalize(this.type)}` as `OTAs${Capitalize<NonNullable<HotelAndMarketDropdownsSelectionOtasSignature['Args']['type']>>}`;
  }

  // Add default value
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get type() {
    return this.args.type || 'rates';
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get value() {
    return this.options.find(
      (option: OtaOption) => option.ota.id === this.args.selectedOTA.id,
    );
  }

  @action handleChange(option: OtaOption): void {
    this.args.selectOta(option.ota);
  }

  <template>
    <PrismSelectInput
      @onChange={{this.handleChange}}
      @options={{this.options}}
      @unsafe_isClampedToOneLine={{true}}
      @value={{this.value}}
    />
  </template>
}
